// src/services/TokenManager.ts

import { getToken } from 'firebase/messaging';
import { messaging } from '../config/firebase';
import logger, { Logger } from '../utils/logger';

export class TokenManager {
  private static instance: TokenManager;
  private static readonly TOKEN_VALID_DURATION = 3600000; // 1 hour
  private static readonly STORAGE_KEY = 'fcm_token_data';
  private static readonly VAPID_KEY = "BChSTS9tcwV1Ring5rMDhbPVtc83NVW6PD0DtGPEduxRcDP0hJztgTHsOcASoU9ZturcQJ2pTCMn1ogbMTsX_aI";

  private tokenData: {
    token: string | null;
    timestamp: number;
    status: 'ready' | 'refreshing' | 'error';
  };

  private constructor() {
    this.tokenData = this.loadTokenData();
  }

  public static getInstance(): TokenManager {
    if (!TokenManager.instance) {
      TokenManager.instance = new TokenManager();
    }
    return TokenManager.instance;
  }

  private loadTokenData() {
    const stored = localStorage.getItem(TokenManager.STORAGE_KEY);
    return stored ? JSON.parse(stored) : {
      token: null,
      timestamp: 0,
      status: 'ready' as const
    };
  }

  private saveTokenData(): void {
    localStorage.setItem(TokenManager.STORAGE_KEY, JSON.stringify(this.tokenData));
  }

  public async getValidToken(): Promise<string> {
    logger.log('🔑 TokenManager: Getting valid token...');
    logger.log('🔑 TokenManager: Current token data:', {
      hasToken: !!this.tokenData.token,
      status: this.tokenData.status,
      age: Date.now() - this.tokenData.timestamp,
      maxAge: TokenManager.TOKEN_VALID_DURATION
    });
    
    if (this.isTokenValid()) {
      logger.log('🔑 TokenManager: ✅ Using existing valid token');
      return this.tokenData.token!;
    }
    logger.log('🔑 TokenManager: ⚠️ Token invalid or missing, refreshing...');
    return this.refreshToken();
  }

  private async refreshToken(): Promise<string> {
    try {
      logger.log('🔑 TokenManager: Starting token refresh...');
      this.tokenData.status = 'refreshing';
      this.saveTokenData();
      logger.log('🔑 TokenManager: Status set to refreshing');
  
      const newToken = await this.requestNewToken();
      logger.log('🔑 TokenManager: ✅ New token received:', newToken ? `${newToken.substring(0, 20)}...` : 'null');
  
      this.tokenData = {
        token: newToken,
        timestamp: Date.now(),
        status: 'ready'
      };
      this.saveTokenData();
      logger.log('🔑 TokenManager: Token data saved to localStorage');
      
      return newToken;
    } catch (error) {
      logger.error('❌ TokenManager: Token refresh failed:', error);
      this.tokenData.status = 'error';
      this.saveTokenData();
      throw error;
    }
  }
  

// In TokenManager.ts
private async requestNewToken(): Promise<string> {
  try {
    logger.log('🔑 TokenManager: Requesting new FCM token...');
    logger.log('🔑 TokenManager: Getting service worker registration...');
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration?.active) {
      logger.error('❌ TokenManager: No active service worker found');
      throw new Error('No active service worker found');
    }
    logger.log('🔑 TokenManager: Service worker registration found:', registration);

    if (!messaging) {
      logger.error('❌ TokenManager: Firebase messaging not initialized');
      throw new Error('Firebase messaging not initialized');
    }
    logger.log('🔑 TokenManager: Firebase messaging available');

    logger.log('🔑 TokenManager: Requesting token with VAPID key...');
    const token = await getToken(messaging, {
      vapidKey: TokenManager.VAPID_KEY,
      serviceWorkerRegistration: registration
    });
    
    if (!token) {
      logger.error('❌ TokenManager: No token received from Firebase');
      throw new Error('No token received from Firebase');
    }
    
    logger.log('🔑 TokenManager: ✅ FCM token received successfully');
    return token;
  } catch (error) {
    logger.error('❌ TokenManager: Error getting new token:', error);
    throw error;
  }
}

  private isTokenValid(): boolean {
    return (
      this.tokenData.token !== null &&
      this.tokenData.status === 'ready' &&
      Date.now() - this.tokenData.timestamp < TokenManager.TOKEN_VALID_DURATION
    );
  }
}

export default TokenManager;